import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SOSAlert {
  id: string;
  status: string;
  trigger_type: string;
  latitude: number | null;
  longitude: number | null;
  created_at: string;
}

interface EmergencyContact {
  id: string;
  name: string;
  phone: string;
  relationship: string | null;
  is_primary: boolean;
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

const FALL_THRESHOLD = 30; // m/s² — significant impact
const FALL_COOLDOWN = 10000; // 10s between detections

export function useSOS() {
  const [activeAlert, setActiveAlert] = useState<SOSAlert | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [fallDetectionEnabled, setFallDetectionEnabled] = useState(false);
  const lastFallTime = useRef(0);

  // Fetch emergency contacts
  const fetchContacts = useCallback(async () => {
    const { data } = await supabase
      .from("emergency_contacts")
      .select("*")
      .order("is_primary", { ascending: false });
    if (data) setContacts(data as EmergencyContact[]);
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  // Get current position
  const getPosition = (): Promise<GeolocationPosition | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) { resolve(null); return; }
      navigator.geolocation.getCurrentPosition(resolve, () => resolve(null), {
        enableHighAccuracy: true, timeout: 5000,
      });
    });
  };

  // Create SOS alert and notify group members
  const triggerSOS = useCallback(async (triggerType: "manual" | "fall_detection" = "manual") => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { toast.error("Debes iniciar sesión"); return; }

      const pos = await getPosition();
      const lat = pos?.coords.latitude ?? null;
      const lng = pos?.coords.longitude ?? null;

      const { data, error } = await supabase
        .from("sos_alerts")
        .insert({
          user_id: user.id,
          trigger_type: triggerType,
          latitude: lat,
          longitude: lng,
          location_accuracy: pos?.coords.accuracy ?? null,
        })
        .select()
        .single();

      if (error) throw error;
      setActiveAlert(data as SOSAlert);
      setChatMessages([]);

      // Initial AI message
      const initialMessage = triggerType === "fall_detection"
        ? "Se ha detectado una posible caída. ¿Estás bien? ¿Puedes responder?"
        : "🚨 SOS activado. ¿Qué ha ocurrido? ¿Hay heridos? Descríbeme la situación.";
      
      setChatMessages([{ role: "assistant", content: initialMessage }]);

      // Notify all group members
      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("display_name")
          .eq("user_id", user.id)
          .single();

        const displayName = profile?.display_name || "Un miembro";

        // Fetch medical details for emergency
        const { data: medData } = await (supabase as any)
          .from("medical_details")
          .select("*")
          .eq("user_id", user.id)
          .single();

        let medicalString = "";
        let medicalPayload = null;
        if (medData?.consent_given) {
          medicalPayload = {
            blood_type: medData.blood_type,
            allergies: medData.allergies,
            medical_conditions: medData.medical_conditions
          };
          medicalString = ` ⛑️ [FICHA MÉDICA: Sangre ${medData.blood_type || 'Desconocida'}, Alergias: ${medData.allergies || 'Ninguna'}, Condiciones: ${medData.medical_conditions || 'Ninguna'}]`;
        }

        // Get all groups the user belongs to
        const { data: memberships } = await supabase
          .from("group_members")
          .select("group_id")
          .eq("user_id", user.id);

        if (memberships && memberships.length > 0) {
          const groupIds = memberships.map(m => m.group_id);
          
          // Get all members from those groups (excluding self)
          const { data: groupMembers } = await supabase
            .from("group_members")
            .select("user_id")
            .in("group_id", groupIds)
            .neq("user_id", user.id);

          if (groupMembers && groupMembers.length > 0) {
            const uniqueUserIds = [...new Set(groupMembers.map(m => m.user_id))];
            
            const locationText = lat && lng
              ? `📍 Coordenadas: ${lat.toFixed(5)}, ${lng.toFixed(5)} — https://www.google.com/maps?q=${lat},${lng}`
              : "📍 Ubicación no disponible";

            const triggerLabel = triggerType === "fall_detection" ? "caída detectada automáticamente" : "activación manual";

            const notifications = uniqueUserIds.map(uid => ({
              user_id: uid,
              title: `🚨 SOS — ${displayName}`,
              body: `Emergencia (${triggerLabel}). ${locationText}.${medicalString} Los servicios de emergencia han sido alertados.`,
              type: "sos_alert",
              data: JSON.stringify({
                alert_id: data.id,
                latitude: lat,
                longitude: lng,
                triggered_by: user.id,
                medical_data: medicalPayload,
              }),
            }));

            await supabase.from("notifications").insert(notifications);

            // Trigger push notifications for SOS
            try {
              await supabase.functions.invoke("send-push", {
                body: {
                  user_ids: uniqueUserIds,
                  title: `🚨 SOS — ${displayName}`,
                  body: `Emergencia (${triggerLabel}). ${locationText}${medicalString}`,
                  type: "sos_alert",
                  data: { alert_id: data.id, latitude: lat, longitude: lng, medical_data: medicalPayload },
                  url: "/dashboard",
                },
              });
            } catch (pushErr) {
              console.warn("Push notification failed (non-blocking):", pushErr);
            }
          }
        }
      } catch (notifyErr) {
        console.error("Error notifying group members:", notifyErr);
        // Don't block SOS for notification failures
      }

      toast.success("🚨 Alerta SOS activada — Grupo notificado", { duration: 5000 });
    } catch (err) {
      console.error("SOS trigger error:", err);
      toast.error("Error al activar SOS. Llama al 112.");
    }
  }, []);

  // Send message to AI first aid
  const sendMessage = useCallback(async (content: string) => {
    if (!activeAlert || isStreaming) return;

    const userMsg: ChatMessage = { role: "user", content };
    const updatedMessages = [...chatMessages, userMsg];
    setChatMessages(updatedMessages);
    setIsStreaming(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No session");

      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sos-first-aid`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            messages: updatedMessages,
            alertId: activeAlert.id,
          }),
        }
      );

      if (!resp.ok || !resp.body) throw new Error("Stream failed");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantContent = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let newlineIdx: number;
        while ((newlineIdx = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, newlineIdx);
          buffer = buffer.slice(newlineIdx + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6).trim();
          if (json === "[DONE]") break;
          try {
            const parsed = JSON.parse(json);
            const delta = parsed.choices?.[0]?.delta?.content;
            if (delta) {
              assistantContent += delta;
              setChatMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last?.role === "assistant" && prev.length > updatedMessages.length) {
                  return prev.map((m, i) =>
                    i === prev.length - 1 ? { ...m, content: assistantContent } : m
                  );
                }
                return [...prev, { role: "assistant", content: assistantContent }];
              });
            }
          } catch { /* partial */ }
        }
      }

      // Save to DB
      if (assistantContent) {
        await supabase.from("sos_chat_messages").insert([
          { alert_id: activeAlert.id, role: "user", content },
          { alert_id: activeAlert.id, role: "assistant", content: assistantContent },
        ]);
      }
    } catch (err) {
      console.error("SOS chat error:", err);
      toast.error("Error de conexión. Llama al 112.");
    } finally {
      setIsStreaming(false);
    }
  }, [activeAlert, chatMessages, isStreaming]);

  // Resolve alert
  const resolveAlert = useCallback(async () => {
    if (!activeAlert) return;
    await supabase
      .from("sos_alerts")
      .update({ status: "resolved", resolved_at: new Date().toISOString() })
      .eq("id", activeAlert.id);
    setActiveAlert(null);
    setChatMessages([]);
    toast.success("Alerta resuelta. ¡Nos alegra que estés bien! 🏁");
  }, [activeAlert]);

  // Fall detection
  const handleMotion = useCallback((event: DeviceMotionEvent) => {
    const acc = event.acceleration;
    if (!acc?.x || !acc?.y || !acc?.z) return;

    const magnitude = Math.sqrt(acc.x ** 2 + acc.y ** 2 + acc.z ** 2);
    const now = Date.now();

    if (magnitude > FALL_THRESHOLD && now - lastFallTime.current > FALL_COOLDOWN) {
      lastFallTime.current = now;
      console.log(`[SOS] Fall detected! Magnitude: ${magnitude.toFixed(1)} m/s²`);
      triggerSOS("fall_detection");
    }
  }, [triggerSOS]);

  const enableFallDetection = useCallback(async () => {
    // Request permission on iOS 13+
    if (typeof (DeviceMotionEvent as any).requestPermission === "function") {
      try {
        const permission = await (DeviceMotionEvent as any).requestPermission();
        if (permission !== "granted") {
          toast.error("Permiso de sensores denegado");
          return;
        }
      } catch {
        toast.error("Error al solicitar permiso de sensores");
        return;
      }
    }

    window.addEventListener("devicemotion", handleMotion);
    setFallDetectionEnabled(true);
    toast.success("🛡️ Detección de caídas activada");
  }, [handleMotion]);

  const disableFallDetection = useCallback(() => {
    window.removeEventListener("devicemotion", handleMotion);
    setFallDetectionEnabled(false);
  }, [handleMotion]);

  // Cleanup
  useEffect(() => {
    return () => {
      window.removeEventListener("devicemotion", handleMotion);
    };
  }, [handleMotion]);

  // Contact management
  const addContact = useCallback(async (contact: Omit<EmergencyContact, "id">) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    const { error } = await supabase.from("emergency_contacts").insert({
      ...contact,
      user_id: user.id,
    });
    if (error) { toast.error("Error al guardar contacto"); return; }
    toast.success("Contacto de emergencia añadido");
    fetchContacts();
  }, [fetchContacts]);

  const removeContact = useCallback(async (id: string) => {
    await supabase.from("emergency_contacts").delete().eq("id", id);
    fetchContacts();
  }, [fetchContacts]);

  return {
    activeAlert,
    chatMessages,
    isStreaming,
    contacts,
    fallDetectionEnabled,
    triggerSOS,
    sendMessage,
    resolveAlert,
    enableFallDetection,
    disableFallDetection,
    addContact,
    removeContact,
    fetchContacts,
  };
}
